import { useContext, useEffect, useMemo, useState } from "react"
import { supabase } from "../supabaseClient"
import { UserContext } from "../UserContext"
import { getValidSupabaseAccessToken } from "../utils/supabaseSession"

const MAX_SCREENSHOTS = 5
const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB matches edge function limit

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp"]

const initialStatus = { type: null, message: "", link: "" }

function BugReportWidget() {
  const user = useContext(UserContext)
  const [isOpen, setIsOpen] = useState(false)
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [screenshots, setScreenshots] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [status, setStatus] = useState(initialStatus)

  const toggleOpen = () => {
    setIsOpen((prev) => !prev)
    setStatus(initialStatus)
  }

  const close = () => {
    setIsOpen(false)
    setStatus(initialStatus)
  }

  useEffect(() => {
    if (!isOpen) return
    const handler = (event) => {
      if (event.key === "Escape") close()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [isOpen])

  const hasScreenshots = screenshots.length > 0

  const remainingSlots = useMemo(
    () => Math.max(0, MAX_SCREENSHOTS - screenshots.length),
    [screenshots.length],
  )

  const resetForm = () => {
    setTitle("")
    setDescription("")
    setScreenshots([])
  }

  const handleFileSelection = async (fileList) => {
    const files = Array.from(fileList || [])
    if (!files.length) return

    const availableSlots = Math.max(0, MAX_SCREENSHOTS - screenshots.length)
    const filesToProcess = files.slice(0, availableSlots)

    const processed = await Promise.all(
      filesToProcess.map((file) =>
        new Promise((resolve, reject) => {
          if (!ACCEPTED_TYPES.includes(file.type)) {
            reject(new Error(`Unsupported file type: ${file.type}`))
            return
          }
          if (file.size > MAX_FILE_SIZE) {
            reject(new Error(`${file.name} exceeds the 5MB limit`))
            return
          }
          const reader = new FileReader()
          reader.onload = () => {
            const result = reader.result
            if (typeof result !== "string") {
              reject(new Error("Failed to read file"))
              return
            }
            const base64 = result.split(",")[1]
            resolve({
              name: file.name,
              type: file.type,
              size: file.size,
              base64,
            })
          }
          reader.onerror = () => reject(new Error(`Failed to read ${file.name}`))
          reader.readAsDataURL(file)
        }).catch((error) => ({ error, name: file.name }))
      ),
    )

    const successes = processed.filter((item) => !item.error)
    const errors = processed.filter((item) => item.error)
    if (errors.length) {
      const message = errors.map((item) => item.error.message).join("\n")
      setStatus({ type: "error", message, link: "" })
    }
    if (successes.length) {
      setScreenshots((prev) => [...prev, ...successes])
    }
  }

  const removeScreenshot = (index) => {
    setScreenshots((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!title.trim() || !description.trim()) {
      setStatus({
        type: "error",
        message: "Title and description are required.",
        link: "",
      })
      return
    }

    setIsSubmitting(true)
    setStatus(initialStatus)

    try {
      const token = await getValidSupabaseAccessToken(supabase)
      const { data, error } = await supabase.functions.invoke("submit-bug-report", {
        body: {
          title: title.trim(),
          description: description.trim(),
          reporterEmail: user?.email || "",
          reporterName: user?.user_metadata?.full_name || user?.email || "",
          screenshots: screenshots.map((item) => ({
            name: item.name,
            type: item.type,
            base64: item.base64,
          })),
        },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })

      if (error) {
        let errorMessage = "Failed to submit bug report."
        try {
          const bodyText = await error.context?.text?.()
          if (bodyText) {
            const parsed = JSON.parse(bodyText)
            if (parsed?.error) errorMessage = parsed.error
          }
        } catch (parseError) {
          console.error("Failed to read error response", parseError)
        }
        setStatus({ type: "error", message: errorMessage, link: "" })
      } else {
        resetForm()
        setStatus({
          type: "success",
          message: "Bug report submitted!",
          link: data?.issueUrl || "",
        })
      }
    } catch (submitError) {
      console.error("Bug report submission failed", submitError)
      setStatus({
        type: "error",
        message: "Unexpected error submitting bug report.",
        link: "",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="bug-report-widget">
      <button
        type="button"
        className="bug-report-toggle"
        onClick={toggleOpen}
        aria-expanded={isOpen}
        aria-controls="bug-report-panel"
      >
        Report a bug
      </button>
      {isOpen && (
        <div className="bug-report-modal" role="dialog" aria-modal="true">
          <div className="bug-report-backdrop" onClick={close} />
          <div className="bug-report-panel" id="bug-report-panel">
            <div className="bug-report-header">
              <h2>Report a bug</h2>
              <button type="button" className="bug-report-close" onClick={close} aria-label="Close">
                ×
              </button>
            </div>
            <form className="bug-report-form" onSubmit={handleSubmit}>
              <label className="bug-report-label">
                Title
                <input
                  className="bug-report-input"
                  type="text"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="Summarize the problem"
                  required
                />
              </label>
              <label className="bug-report-label">
                Description
                <textarea
                  className="bug-report-textarea"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="What happened? Include steps to reproduce."
                  rows={5}
                  required
                />
              </label>
              <div className="bug-report-label">
                <div className="bug-report-label-row">
                  <span>Screenshots</span>
                  <span className="bug-report-help">Optional · up to {MAX_SCREENSHOTS} files</span>
                </div>
                <input
                  type="file"
                  accept={ACCEPTED_TYPES.join(",")}
                  multiple
                  onChange={(event) => {
                    handleFileSelection(event.target.files)
                    event.target.value = ""
                  }}
                  disabled={remainingSlots === 0}
                />
                {remainingSlots === 0 && <p className="bug-report-help">Maximum screenshots selected</p>}
                {hasScreenshots && (
                  <ul className="bug-report-screenshot-list">
                    {screenshots.map((file, index) => (
                      <li key={index} className="bug-report-screenshot-item">
                        <span className="bug-report-screenshot-name">{file.name}</span>
                        <button
                          type="button"
                          className="bug-report-remove"
                          onClick={() => removeScreenshot(index)}
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {status.type && (
                <div className={`bug-report-status bug-report-status-${status.type}`}>
                  <span>{status.message}</span>
                  {status.link && (
                    <a
                      className="bug-report-status-link"
                      href={status.link}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View issue
                    </a>
                  )}
                </div>
              )}
              <div className="bug-report-actions">
                <button type="button" onClick={close} className="bug-report-secondary">
                  Cancel
                </button>
                <button type="submit" className="bug-report-primary" disabled={isSubmitting}>
                  {isSubmitting ? "Submitting…" : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default BugReportWidget
